import { useEffect, useState } from "react";
import { InputAdornment } from "@mui/material";
import { LuInfo } from "react-icons/lu";
import ModalCommon from "components/ModalCommon";
import { toast } from "react-toastify";
import * as S from "./styles";
import VincularFichaModal from "../VincularFichaModal";

interface Data {
  editavel?: boolean;
  nomeComprador: string;
  codigoFicha: string | undefined;
  rendaFamiliar: string;
  totalAprovadoFinanciamento: string;
  valorFgts: string;
  subsidio: string;
  valorCapacidadeMensal: string;
  beneficioExtra: string;
  nomeStatus?: string;
}

interface Props {
  formCredito: Data;
  setFormCredito: any;
  isLoadingFicha: boolean;
  refetchFicha: () => void;
  dataFicha: Data;
  handleVincularCredito: () => void;
  mutateResumoPagamento: () => void;
  handleValidateDadosAvaliacaoAto: any;
  errorNomeComprador: boolean;
  errorRendaFamiliar: boolean;
  errorTotalAprovadoFinanciamento: boolean;
  errorValorCapacidadeMensal: boolean;
  setErrorNomeComprador: any;
  setErrorRendaFamiliar: any;
  setErrorTotalAprovadoFinanciamento: any;
  setErrorValorCapacidadeMensal: any;
  validateFields: any;
  isFichaLinked: boolean;
  setIsFichaLinked: any;
  handleCodigoFichaChange: any;
  isErrorFicha: boolean;
}

export default function DadosAvaliacaoCredito(props: Props) {
  const [modalVincularFicha, setModalVincularFicha] = useState(false);

  const formatCurrency = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue === "00") {
      return "";
    }
    const formattedValue = (Number(cleanedValue) / 100).toFixed(2);
    return `${formattedValue
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  };

  useEffect(() => {
    if (
      !props.isLoadingFicha &&
      modalVincularFicha &&
      props.dataFicha &&
      !props.isErrorFicha
    ) {
      props.setFormCredito({ ...props.dataFicha });
      setModalVincularFicha(false);
      props.validateFields({ ...props.dataFicha });
      props.setIsFichaLinked(true);
    }
    if (!props.isLoadingFicha && modalVincularFicha && props.isErrorFicha) {
      setModalVincularFicha(false);
    }
  }, [props.isLoadingFicha]);

  return (
    <S.Container>
      <S.Title>Dados da Avaliação de Crédito</S.Title>
      <S.BodyContainer>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Nome Comprador *</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              onBlur={props.handleValidateDadosAvaliacaoAto}
              error={props.errorNomeComprador}
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onChange={(e: any) => {
                props.setFormCredito({
                  ...props.formCredito,
                  nomeComprador: e.target.value,
                });
                if (props.errorNomeComprador) {
                  props.setErrorNomeComprador(false);
                }
              }}
              value={props.formCredito?.nomeComprador}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>
              Código Ficha
              {props.formCredito?.nomeStatus && (
                <S.StatusBadge status={props.formCredito.nomeStatus}>
                  {props.formCredito.nomeStatus}
                </S.StatusBadge>
              )}
            </S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              type="number"
              disabled={props.dataFicha?.editavel === false}
              onChange={(e) => {
                props.setFormCredito({
                  ...props.formCredito,
                  codigoFicha: e?.target?.value,
                });
                props.handleCodigoFichaChange(e.target.value);
                if (e.target.value === "") {
                  props.setFormCredito({
                    nomeComprador: "",
                    codigoFicha: "",
                    rendaFamiliar: "",
                    totalAprovadoFinanciamento: "",
                    valorFgts: "",
                    subsidio: "",
                    valorCapacidadeMensal: "",
                    beneficioExtra: "",
                  });
                }
              }}
              value={props.formCredito?.codigoFicha}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ paddingRight: 0 }}>
                    <S.ButtonVincular
                      disabled={
                        props.formCredito?.codigoFicha === "" ||
                        props.dataFicha?.editavel === false
                      }
                      onClick={() => setModalVincularFicha(true)}
                    >
                      Vincular
                    </S.ButtonVincular>
                  </InputAdornment>
                ),
              }}
            />
            {props.formCredito?.codigoFicha === "" && (
              <S.WarningMessage>
                <LuInfo />
                ﾠNão tem uma ficha vinculada nessa proposta
              </S.WarningMessage>
            )}
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Renda Familiar (Renda Bruta) *</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              onBlur={props.handleValidateDadosAvaliacaoAto}
              error={props.errorRendaFamiliar}
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onChange={(e: any) => {
                props.setFormCredito({
                  ...props.formCredito,
                  rendaFamiliar: formatCurrency(e.target.value),
                });
                if (props.errorRendaFamiliar) {
                  props.setErrorRendaFamiliar(false);
                }
              }}
              value={props.formCredito?.rendaFamiliar}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>Total Aprovado de Financiamento *</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onBlur={() =>
                props.handleValidateDadosAvaliacaoAto(
                  "totalAprovadoFinanciamento"
                )
              }
              error={props.errorTotalAprovadoFinanciamento}
              onChange={(e: any) => {
                props.setFormCredito({
                  ...props.formCredito,
                  totalAprovadoFinanciamento: formatCurrency(e.target.value),
                });
                if (props.errorTotalAprovadoFinanciamento) {
                  props.setErrorTotalAprovadoFinanciamento(false);
                }
              }}
              value={props.formCredito?.totalAprovadoFinanciamento}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Valor FGTS</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onBlur={props.handleValidateDadosAvaliacaoAto}
              onChange={(e: any) =>
                props.setFormCredito({
                  ...props.formCredito,
                  valorFgts: formatCurrency(e.target.value),
                })
              }
              value={props.formCredito?.valorFgts}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>Subsídio</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onBlur={props.handleValidateDadosAvaliacaoAto}
              onChange={(e: any) =>
                props.setFormCredito({
                  ...props.formCredito,
                  subsidio: formatCurrency(e.target.value),
                })
              }
              value={props.formCredito?.subsidio}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
        <S.Row>
          <S.Column>
            <S.TitleInfo>Beneficio Extra</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={props.dataFicha?.editavel === false}
              onBlur={props.handleValidateDadosAvaliacaoAto}
              onChange={(e: any) => {
                props.setFormCredito({
                  ...props.formCredito,
                  beneficioExtra: formatCurrency(e.target.value),
                });
              }}
              value={props.formCredito?.beneficioExtra}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
          <S.Column>
            <S.TitleInfo>Valor Capacidade Pagamento Mensal (PMT) *</S.TitleInfo>
            <S.TextField
              InputLabelProps={{ shrink: false }}
              variant="outlined"
              fullWidth
              disabled={
                props.isFichaLinked || props.dataFicha?.editavel === false
              }
              onBlur={props.handleValidateDadosAvaliacaoAto}
              error={props.errorValorCapacidadeMensal}
              onChange={(e: any) => {
                props.setFormCredito({
                  ...props.formCredito,
                  valorCapacidadeMensal: formatCurrency(e.target.value),
                });
                if (props.errorValorCapacidadeMensal) {
                  props.setErrorValorCapacidadeMensal(false);
                }
              }}
              value={props.formCredito?.valorCapacidadeMensal}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">R$</InputAdornment>
                ),
                inputProps: {
                  style: { textAlign: "right" },
                },
              }}
            />
          </S.Column>
        </S.Row>
      </S.BodyContainer>
      <ModalCommon
        open={modalVincularFicha}
        setOpenModal={setModalVincularFicha}
        onClose={() => {
          setModalVincularFicha(false);
        }}
      >
        <VincularFichaModal
          onClose={() => setModalVincularFicha(false)}
          handleVincular={props.handleVincularCredito}
          isLoading={props?.isLoadingFicha}
        />
      </ModalCommon>
    </S.Container>
  );
}
